import { useCallback, useEffect, useRef, useState } from 'react'

import { useEffectOnce } from './useEffectOnce'

// reason for 'any':
// error TS4078: Parameter 'response' of exported function has or is using private name 'Response'
export function toJson(response) {
  return response
    .json()
    .catch((error) => {
      throw response
    })
    .then((json) => {
      if (!response.ok) {
        // if (typeof json === 'object' && !('code' in json)) json.code = response.status
        throw json
      }
      return json
    })
}

export function toRaw(response) {
  return response
    .text()
    .catch((error) => {
      throw response
    })
    .then((text) => {
      if (!response.ok) {
        throw text
      }
      return text
    })
    .catch((error) => {
      throw response
    })
}

export function useDeferredQuery(fetchUrl, defaultValue, transformer) {
  const [value, setValue] = useState(defaultValue || null)
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const latestFetchUrlRef = useRef(fetchUrl)
  const loadingRef = useRef(false)
  const abortControllerRef = useRef(new AbortController())

  const fetchValue = useRef(() => {
    if (!latestFetchUrlRef.current) return new Promise((resolve, reject) => resolve(defaultValue))

    loadingRef.current = true
    setLoading(true)
    setLoaded(false)
    setError(false)
    setErrorMessage(null)

    return fetch(latestFetchUrlRef.current, { signal: abortControllerRef.current.signal })
      .then(transformer)
      .then((value) => {
        loadingRef.current = false
        setLoading(false)
        setLoaded(true)
        setValue(value)
        return value
      })
      .catch((e) => {
        loadingRef.current = false
        setLoading(false)
        setLoaded(false)
        if (e.name === 'AbortError') {
          setError(false)
          setErrorMessage('Request aborted')
        } else {
          setError(true)
          let message = ''
          if (e.status) message += `Error ${e.status}: `
          if (e.statusText) message += e.statusText
          setErrorMessage(message || 'Error fetching')
        }
      })
  })

  const cancelFetch = useCallback(() => {
    if (loadingRef.current) abortControllerRef.current.abort()
  }, [])

  useEffect(() => {
    latestFetchUrlRef.current = fetchUrl
    if (fetchUrl) setValue(defaultValue || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUrl])

  return {
    value: value,
    fetch: fetchValue.current,
    cancel: cancelFetch,
    loaded,
    loading,
    error,
    errorMessage,
    fetchUrl,
  }
}

export function useQuery(fetchUrl, defaultValue, transformer) {
  const { value, fetch, cancel, loaded, loading, error, errorMessage } = useDeferredQuery(
    fetchUrl,
    defaultValue,
    transformer
  )
  const latestFetchUrlRef = useRef(fetchUrl)

  useEffectOnce(() => {
    if (fetchUrl) fetch()
    return () => {
      cancel()
    }
  })

  useEffect(() => {
    if (latestFetchUrlRef.current !== fetchUrl) {
      console.log('fetch url changed', latestFetchUrlRef.current, fetchUrl)
      if (fetchUrl) fetch()
      latestFetchUrlRef.current = fetchUrl
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUrl])

  return [
    loading || error ? defaultValue || null : value,
    {
      loaded,
      loading,
      error,
      errorMessage,
      fetch,
      cancel,
    },
  ]
}

export function useJsonQuery(fetchUrl, defaultValue) {
  return useQuery(fetchUrl, defaultValue, toJson)
}

export function useRawQuery(fetchUrl, defaultValue) {
  return useQuery(fetchUrl, defaultValue, toRaw)
}
